import { useCallback, useEffect } from "react";
import PageTitle from "@/components/PageTitle";
import { Outlet, useParams, useLocation } from "react-router-dom";
import type { Path } from "@/router";
import AppLayout from "@/layouts/AppLayout";
import Breadcrumb from "@/components/Breadcrumb";
import Banner from "@/components/Banner";
import { useState, useMemo } from "react";
import ObjectHeading from "@/components/ObjectHeading";
import { ObjectHeaderSkeleton } from "@/components/Skeleton";
import EventTabs from "../../_EventTabs";
import { useMutation, useQuery, useApolloClient } from "@apollo/client";
import {
  EVENT_REGISTRANTS_QUERY,
  PAT_WORKSHEET_MUTATION,
  SCAFFOLD_EVENT_REGISTRANTS,
  EVENT_LEADS_QUERY
} from "../queries";
import { RECRUITMENT_EVENT_OPTIONS_QUERY } from "../../queries";
import { Event, EventWrapper } from "../types";
import { RecruitmentEventOptionsQueryResponse } from "../../types";
import { buildMyPgaUrl, isDev, openBrowser } from "@/lib/urlUtils";
import { useReturnTo } from "@/hooks/useSearchParams";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import CancelEventModal, { CancelEvent } from "./cancellations/_components/CancelEventModal";
import CopyRegistrationOverrideLinkModal from "../../_components/CopyRegistrationOverrideLinkModal";

import { showSnackbar } from "@/lib/snackbarUtils";
import { renderEventStatus } from "../../_utils";
import { toFormat, isSameDay } from "@/lib/dateHelpers";
import type { Action } from "@/components/MenuButton";
import { useNavigate } from "@/router";
import { useHasRoles, useCanViewRecruitingEventRecapsBool, useCanManageTasksBool } from "@/hooks/useCapabilities";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";
import DownloadQRCodeDialog from "./recap/_components/DownloadQRCodeDialog";
import { downloadRecruitmentEventCsv, getEventTypeName } from "../utils";
import { useEventsBasePath } from "../../_eventsBasePathContext";
import { useEventDetailQueryDocument } from "../useEventDetailQuery";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";
import { usePinnedNotes } from "@/features/global/components/NotesTable";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import AddTaskSideSheet from "@/features/global/components/AddTaskSideSheet/AddTaskSideSheet";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { EVENT_NOTES_QUERY, type EventNotesQueryResponse } from "./notes/queries";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import { type CRMNote, NoteNotableValues, NotePriorityValues } from "@/types";

export interface EventOutletContext {
  setErrorMessage: (message: string | null) => void;
  eventData: EventWrapper["event"] | undefined;
  eventLoading: boolean;
  eventRefetch: () => void;
  enumOptionsData?: RecruitmentEventOptionsQueryResponse;
}

export default function EventPage() {
  const { eventSlug = "" } = useParams();
  const basePath = useEventsBasePath();
  const eventDetailQuery = useEventDetailQueryDocument();
  const returnTo = useReturnTo();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCompactLayout } = useIsCompactLayout();
  const { addRecentlyVisitedItem } = useRecentlyVisited();
  const client = useApolloClient();
  const { trackEvent } = useAmplitude();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCopyOverrideLinkModalOpen, setIsCopyOverrideLinkModalOpen] = useState(false);
  const [isQrCodeDownloadModalOpen, setIsQrCodeDownloadModalOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const currentUser = useCurrentBackofficeUser();
  const canManageTasks = useCanManageTasksBool();

  function onClickCancel(eventData: Event | undefined) {
    if (!eventData) {
      return;
    }
    setIsCancelModalOpen(true);
  }

  const [scaffoldEventRegistrants] = useMutation<{
    scaffoldEventRegistrants: { success: boolean; message?: string };
  }>(SCAFFOLD_EVENT_REGISTRANTS, { refetchQueries: [EVENT_REGISTRANTS_QUERY] });

  const {
    data: eventDataResponse,
    loading,
    refetch
  } = useQuery<EventWrapper>(eventDetailQuery, {
    variables: { slug: eventSlug }
  });

  const eventData = eventDataResponse?.event;
  const isRecruitment = getEventTypeName(eventData) === "Recruitment";

  const { data: notesData } = useQuery<EventNotesQueryResponse>(EVENT_NOTES_QUERY, {
    variables: {
      slug: eventSlug,
      input: { priority: [NotePriorityValues.PROMOTE, NotePriorityValues.ALERT] }
    },
    skip: !eventSlug
  });

  const eventNotes = notesData?.eventNotes?.nodes ?? [];
  const { pinnedNotes, shouldAutoOpenNotes } = usePinnedNotes({
    notes: eventNotes,
    entityId: eventData?.id ?? eventSlug,
    sessionStorageKey: "visitedEventDetails"
  });

  const headingPinnedNotes = pinnedNotes
    ? {
        ...pinnedNotes,
        onViewAll: () => navigate(`${basePath}/${eventSlug}/notes` as Path),
        getNoteAction: (note: CRMNote) => () =>
          navigate(`${basePath}/${eventSlug}/notes?highlightNote=${note.id}` as Path)
      }
    : undefined;

  // Fetch enum options for CSV export and Recap form - only for recruitment events
  const { data: enumOptionsData } = useQuery<RecruitmentEventOptionsQueryResponse>(RECRUITMENT_EVENT_OPTIONS_QUERY, {
    skip: !isRecruitment
  });

  useEffect(() => {
    if (eventData) {
      addRecentlyVisitedItem({
        url: window.location.pathname,
        text: eventData.title,
        subtext: eventData.facility?.name || "",
        image: "today",
        type: "events"
      });
    }
  }, [eventData, addRecentlyVisitedItem]);
  const isPatFinalized = !!eventData?.patEventAttributes?.finalized;
  const isRecruitingFinalized = !!eventData?.recruitmentEventAttributes?.isFinalized;
  const isPayoutSubmitted = !!eventData?.patEventAttributes?.submittedForPaymentAt;
  const isStatusPending = eventData?.status === "Pending";
  const isPat = getEventTypeName(eventData) === "PAT";
  const isSeminar = getEventTypeName(eventData)?.toLowerCase().includes("seminar");
  const hasLateCancellation = eventData?.hasLateCancellation;
  const canCopyRegistrationOverrideLink = useHasRoles(eventData?.overrideRoles ?? []) && !eventData?.finalizedAt;
  const canCopyRegistrationLink = useHasRoles([
    ROLE.GLOBAL_ADMIN,
    ROLE.MEMBERSHIP_SERVICES_STAFF,
    ROLE.MEMBERSHIP_SERVICES_ADMIN
  ]);
  const canExportRecruitingEventDetails = useCanViewRecruitingEventRecapsBool();
  const isSectionScoped = useIsSectionScopedForEvents();
  const canAccessActivityManagement =
    useActivityTypePermissions(eventData?.activityType ?? { title: getEventTypeName(eventData) }) &&
    (!isSectionScoped || !!eventData?.section?.permissions?.manageEvents);

  const onAddRegistrants = useCallback(
    async (event: Event | undefined) => {
      if (!event) {
        return;
      }

      try {
        const { data } = await scaffoldEventRegistrants({
          variables: { id: event.id }
        });
        const { success, message } = data?.scaffoldEventRegistrants || {};

        if (success) {
          showSnackbar("Registrants added", { close: true });
        } else {
          setErrorMessage(message || "");
        }
      } catch (e) {
        setErrorMessage("general error");
      }
    },
    [scaffoldEventRegistrants]
  );

  const [generateWorksheetMutation] = useMutation(PAT_WORKSHEET_MUTATION);

  const handleDownloadPATWorksheet = useCallback(async () => {
    try {
      const { data } = await generateWorksheetMutation({
        variables: { id: eventData?.id }
      });
      const { success, message, url } = data?.patGenerateWorksheet ?? {};

      if (success) {
        window.open(url, "_blank", "noopener,noreferrer");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.worksheetDownloaded,
          getEventAnalyticsData({
            source: "results",
            eventId: eventData?.id,
            eventSlug,
            action: "download"
          })
        );
      } else {
        showSnackbar(message);
      }
    } catch (error) {
      setErrorMessage("Error generating worksheet");
    }
  }, [generateWorksheetMutation, eventData?.id, trackEvent, eventSlug]);

  const handleDownloadLeadFormQRCode = useCallback(() => {
    if (!eventData?.recruitmentLeadFormUrl) {
      showSnackbar("No lead form URL available for this event");
      return;
    } else {
      setIsQrCodeDownloadModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData?.recruitmentLeadFormUrl, showSnackbar]);

  const handleExportEventDetails = useCallback(async () => {
    if (!eventData) {
      return;
    }

    try {
      // Fetch leads count on demand
      const { data: leadsData } = await client.query({
        query: EVENT_LEADS_QUERY,
        variables: {
          slug: eventSlug,
          status: "ALL"
        },
        fetchPolicy: "network-only" // Always fetch fresh data
      });

      const totalLeadsCount = leadsData?.eventRegistrants?.totalCount || 0;
      downloadRecruitmentEventCsv(eventData, totalLeadsCount, eventSlug, enumOptionsData);
    } catch (error) {
      console.error("Error fetching leads count for export:", error);
      setErrorMessage("Error exporting event details");
    }
  }, [client, eventData, eventSlug, enumOptionsData]);

  const actions = useMemo<Action[]>(() => {
    if (loading || !eventData) return [];

    const buttons: Action[] = [];
    const canEdit = (eventData.status === "Active" || eventData.status === "Draft") && canAccessActivityManagement;
    const exportButtonShow = isRecruitment && (!isRecruitingFinalized || canExportRecruitingEventDetails);

    if (canEdit) {
      buttons.push({
        text: "Edit Event",
        variant: "filled",
        onClick: () => {
          const returnTo = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
          const editPath = `${basePath}/${eventSlug}/edit?return_to=${returnTo}`;
          navigate(editPath as Path, { state: { event: eventData } });
          window.scrollTo(0, 0);
        }
      });
    }

    if (eventData.status !== "Draft") {
      buttons.push({
        text: eventData.recruitmentEventAttributes ? "Lead Form" : "Published Page",
        iconName: "open_in_new",
        onClick: () => {
          if (eventData.recruitmentEventAttributes && eventData.recruitmentLeadFormUrl) {
            openBrowser(eventData.recruitmentLeadFormUrl);
          } else {
            openBrowser(buildMyPgaUrl("member/events", eventSlug));
          }
        },
        section: "Manage"
      });
    } else {
      buttons.push({
        text: "View Preview",
        iconName: "open_in_new",
        onClick: () => {
          if (eventData.recruitmentEventAttributes && eventData.recruitmentLeadFormUrl) {
            openBrowser(eventData.recruitmentLeadFormUrl);
          } else {
            openBrowser(buildMyPgaUrl("member/events", eventSlug));
          }
        },
        section: "Manage"
      });
    }

    if (isPat) {
      buttons.push({
        text: "PAT Hub",
        iconName: "open_in_new",
        onClick: () => openBrowser("https://sites.google.com/pgahq.com/pat/home"),
        section: "Manage"
      });
    }

    const copyRegistrationOverrideLinkAction: Action = {
      text: "Copy Registration Override Link",
      onClick: () => setIsCopyOverrideLinkModalOpen(true),
      section: "Manage"
    };
    if (isPat && canCopyRegistrationLink) {
      buttons.push(copyRegistrationOverrideLinkAction);
    } else if (!isPat && canCopyRegistrationOverrideLink) {
      buttons.push(copyRegistrationOverrideLinkAction);
    }

    if (exportButtonShow) {
      buttons.push({
        text: "Event Details",
        iconName: "download",
        onClick: handleExportEventDetails,
        section: "Sharing"
      });
    }

    if (eventData.recruitmentEventAttributes) {
      buttons.push({
        text: "Lead Form QR Code",
        iconName: "download",
        onClick: () => handleDownloadLeadFormQRCode(),
        section: "Sharing"
      });
    }

    if (isPat) {
      buttons.push({
        text: "PAT Worksheet",
        iconName: "download",
        onClick: () => handleDownloadPATWorksheet(),
        section: "Sharing"
      });
    }

    if (isDev()) {
      buttons.push({
        text: "Add Registrants",
        iconName: "build",
        onClick: () => onAddRegistrants(eventData),
        section: "Admin/Dev Only"
      });
    }

    if (canAccessActivityManagement) {
      buttons.push({
        text: "Add Note",
        variant: "outlined",
        onClick: () => setAddNoteOpen(true),
        testId: "add-note-action",
        section: "Actions"
      });
    }

    if (canManageTasks) {
      buttons.push({
        text: "Add Task",
        variant: "outlined",
        onClick: () => setAddTaskOpen(true),
        testId: "add-task-action",
        section: "Actions"
      });
    }

    if (eventData.status === "Active" && canAccessActivityManagement) {
      buttons.push({
        text: "Cancel Event",
        destructive: true,
        onClick: () => onClickCancel(eventData)
      });
    }

    return buttons;
  }, [
    loading,
    eventData,
    eventSlug,
    navigate,
    isRecruitment,
    isRecruitingFinalized,
    canExportRecruitingEventDetails,
    handleExportEventDetails,
    handleDownloadPATWorksheet,
    handleDownloadLeadFormQRCode,
    isPat,
    canCopyRegistrationLink,
    canCopyRegistrationOverrideLink,
    basePath,
    canAccessActivityManagement,
    canManageTasks,
    onAddRegistrants
  ]);

  const contextValue = {
    setErrorMessage,
    eventData,
    eventLoading: loading,
    eventRefetch: refetch,
    enumOptionsData
  };

  const getBannerProps = () => {
    if (loading || !eventData) {
      return null;
    }

    if (isStatusPending && isRecruitment && !isRecruitingFinalized) {
      return {
        closable: false,
        hideSecondaryDismiss: true,
        primaryAction: {
          label: "Go to Recap",
          link: "recap",
          isInternal: true
        },
        description: "Finish all Recap sections and select 'Finalize Recap' to complete this event.",
        variant: "warn" as const,
        testId: "recap-banner"
      };
    } else if (isStatusPending && !isPatFinalized && !isPayoutSubmitted) {
      return {
        closable: false,
        hideSecondaryDismiss: true,
        primaryAction: {
          label: "Go to Results",
          link: "results",
          isInternal: true
        },
        description: "Results and Payout submissions are both pending on this event",
        variant: "warn" as const,
        testId: "results-and-payouts-banner"
      };
    } else if (isStatusPending && isPayoutSubmitted && !isPatFinalized) {
      return {
        closable: false,
        hideSecondaryDismiss: true,
        primaryAction: {
          label: "Go to Results",
          link: "results",
          isInternal: true
        },
        description: "Results submission is pending on this event",
        variant: "warn" as const,
        testId: "results-banner"
      };
    } else if (isStatusPending && !isPayoutSubmitted && isPatFinalized) {
      return {
        closable: false,
        hideSecondaryDismiss: true,
        primaryAction: {
          label: "Go to Payouts",
          link: "payouts",
          isInternal: true
        },
        description: "Payout submission is pending on this event",
        variant: "warn" as const,
        testId: "payouts-banner"
      };
    }
    return null;
  };

  const bannerProps = getBannerProps();

  const renderDesktopLayout = () => (
    <>
      {/* Desktop Banner rendering - positioned at the top */}
      {!isCompactLayout && errorMessage && <Banner closable={true} description={errorMessage} variant="error" />}
      {!isCompactLayout && bannerProps && <Banner {...bannerProps} />}
      <div>
        <PageTitle pattern={location.pathname} title={`Events | ${eventData?.title}`} />
        {loading && !eventData ? (
          <ObjectHeaderSkeleton />
        ) : eventData ? (
          <ObjectHeading
            title={eventData.title}
            metaData={[
              ...((isPat || isSeminar) && eventData.eventCode
                ? [{ text: eventData.eventCode, label: "Event ID" }]
                : []),
              ...(eventData.facility?.name
                ? [
                    {
                      text: eventData.facility.name,
                      label: "Facility",
                      ...(eventData.facility.id ? { href: `/facilities/${eventData.facility.id}/details` } : {})
                    }
                  ]
                : []),
              {
                text: isSameDay(eventData.startsAt, eventData.endsAt, eventData.timezone)
                  ? toFormat(eventData.startsAt) || ""
                  : `${toFormat(eventData.startsAt)} - ${toFormat(eventData.endsAt)}`,
                label: "Date"
              }
            ]}
            iconName="today"
            isDetailsView
            tag={renderEventStatus(eventData.status)}
            actions={actions}
            showFullMetadataInCondensed={true}
            pinnedNotes={headingPinnedNotes}
            initialNotesDialogOpen={shouldAutoOpenNotes}
          />
        ) : null}
        <EventTabs
          eventData={eventData}
          isPatFinalized={isPatFinalized}
          isPayoutSubmitted={isPayoutSubmitted}
          isStatusPending={isStatusPending}
          hasLateCancellation={hasLateCancellation}
        />
        <div data-testid="registrants-page">
          <Outlet context={contextValue} />
        </div>
      </div>
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        compactOnly
        compactTitle="Event Details"
        backLink={{ to: "/events" }}
        onBackClick={() => {
          const backTo = returnTo || basePath;
          navigate(backTo as Path);
        }}
      />
      {/* Mobile Banner rendering - positioned under the page header */}
      {errorMessage && <Banner closable={true} description={errorMessage} variant="error" />}
      {bannerProps && <Banner {...bannerProps} />}
      {renderDesktopLayout()}
    </>
  );

  const isStandalone = basePath === "/events" || basePath === "/activities/events";
  const content = (
    <>
      {isCompactLayout ? renderMobileLayout() : renderDesktopLayout()}

      {/* Modals */}
      {isCancelModalOpen && (
        <CancelEventModal
          event={eventData as CancelEvent}
          open={isCancelModalOpen}
          setOpen={setIsCancelModalOpen}
          refetch={refetch}
        />
      )}
      {isCopyOverrideLinkModalOpen && eventData && (
        <CopyRegistrationOverrideLinkModal
          eventId={eventData.id}
          eventSlug={eventSlug}
          onClosed={() => {
            setIsCopyOverrideLinkModalOpen(false);
          }}
        />
      )}
      {isQrCodeDownloadModalOpen && eventData?.recruitmentLeadFormUrl && (
        <DownloadQRCodeDialog
          open={isQrCodeDownloadModalOpen}
          onClose={() => setIsQrCodeDownloadModalOpen(false)}
          url={eventData.recruitmentLeadFormUrl}
        />
      )}
      {canAccessActivityManagement && eventData && (
        <AddEditNoteSideSheet
          open={addNoteOpen}
          onClose={() => setAddNoteOpen(false)}
          notableId={eventData.id}
          notableType={NoteNotableValues.EVENT}
          refetchQueries={[EVENT_NOTES_QUERY]}
          currentUserId={currentUser?.id}
          eventSlug={eventSlug}
          objectType="event"
        />
      )}
      {canManageTasks && eventData && (
        <AddTaskSideSheet
          open={addTaskOpen}
          onClose={() => setAddTaskOpen(false)}
          currentUser={currentUser}
          defaultReferences={[{ referenceableId: eventData.id, referenceableType: "EVENT" }]}
          refetchQueries={[TASKS_QUERY]}
        />
      )}
    </>
  );

  return isStandalone ? (
    <AppLayout navLinks={[]} interior bottomBar>
      {content}
    </AppLayout>
  ) : (
    content
  );
}
